import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, BosDurum, Buton, Iskelet, Kart } from "@koclukpanel/ui";
import { ApiHatasi, esikDurumu, esikGuncelle } from "./api";

/**
 * AKADEMİK EŞİKLER — F-TPC-02 / F-EXM-11
 *
 * ================================================================
 * "SİSTEM SAĞLIĞI" EKRANINDA, "MÜFREDAT"TA DEĞİL
 * ================================================================
 * Akademik bir ayar olduğu için ilk düşündüğüm yer Müfredat ekranıydı.
 * Ama izin `sistem.ayar.duzenle` ve o izni taşıyan TECH_ADMIN'de
 * `akademik.mufredat.goruntule` YOK — Müfredat menüsüne hiç
 * ulaşamıyor. Ekranı oraya koysaydım, ayarı değiştirebilecek iki
 * kişiden biri onu hiç göremezdi.
 *
 * ================================================================
 * ARALIĞI SUNUCU SÖYLÜYOR
 * ================================================================
 * Ekran kendi sınırını yazmıyor; `sinirlar` sunucudan geliyor. Kendi
 * yazsaydık, sunucudaki aralık değiştiği gün ekran kabul edilmeyecek
 * bir değeri "geçerli" gösterirdi.
 *
 * ================================================================
 * KIRPMA YOK
 * ================================================================
 * Sınır dışı değeri sunucu REDDEDİYOR, sessizce sınıra çekmiyor:
 * kullanıcı %20 yazıp %40 uygulandığını bilmezse, sistemin söylediği
 * yanlış olur. Ekran da o yüzden yazılanı değiştirmiyor, hatayı
 * gösteriyor.
 */
export function AkademikEsikler() {
  const qc = useQueryClient();
  const [basari, setBasari] = useState<string>("");
  const [enAzSoru, setEnAzSoru] = useState<string>("");
  const [ozet, setOzet] = useState<string | null>(null);

  const durum = useQuery({ queryKey: ["akademik-esik"], queryFn: esikDurumu, retry: false });

  const kaydet = useMutation({
    mutationFn: () =>
      esikGuncelle({
        ...(basari.trim() ? { denemeBasariEsigi: Number(basari) / 100 } : {}),
        ...(enAzSoru.trim() ? { konuHukmuIcinEnAzSoru: Number(enAzSoru) } : {}),
      }),
    onSuccess: async (s) => {
      /*
        ETKİ ÖZETİ GÖSTERİLİYOR. Eşik değişince GEÇMİŞE DOKUNULMUYOR
        (Emre'nin kararı 31.07.2026) — kayıtlı konu durumları aynen
        kalıyor. Kaç konunun yeni eşiğe göre farklı değerlendirileceği
        söylenmezse, uzman "hiçbir şey olmadı" sanır ve kararın
        sonucunu göremez.
      */
      const parcalar: string[] = ["Eşikler kaydedildi."];
      if (s.etki.etkilenen > 0) {
        parcalar.push(
          `${s.etki.etkilenen} konu yeni eşiğe göre FARKLI değerlendirilirdi — kayıtlı durumlar değişmedi, isterseniz tek tek yeniden değerlendirin.`,
        );
      } else {
        parcalar.push("Hiçbir konunun değerlendirmesi değişmezdi.");
      }
      if (s.etki.soruEsigiOlculemedi) {
        parcalar.push(
          "Soru sayısı eşiğinin geçmişe etkisi ÖLÇÜLEMİYOR: kayıtlarda başarı oranı var, o oranın kaç soruyla hesaplandığı yok.",
        );
      }
      setOzet(parcalar.join(" "));
      setBasari("");
      setEnAzSoru("");
      await qc.invalidateQueries({ queryKey: ["akademik-esik"] });
    },
  });

  if (durum.isPending) return <Iskelet satir={3} />;

  if (durum.isError) {
    return (
      <Kart baslik="Akademik eşikler">
        <BosDurum
          ton="hata"
          baslik="Eşikler alınamadı"
          aciklama={durum.error instanceof ApiHatasi ? durum.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void durum.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  const s = durum.data.sinirlar;
  const e = durum.data.esikler;
  const v = durum.data.varsayilan;

  return (
    <Kart
      baslik="Akademik eşikler"
      aciklama="Bir konunun “denemede doğrulandı” sayılması için gereken başarı oranı ve hüküm verilebilmesi için gereken en az soru sayısı."
    >
      <div className="flex flex-col gap-3">
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-notr-600">Deneme başarı eşiği</dt>
          <dd className="text-notr-900 tabular-nums">
            %{Math.round(e.denemeBasariEsigi * 100)}
            {e.denemeBasariEsigi !== v.denemeBasariEsigi && (
              <span className="text-notr-600 ml-2 text-xs">
                (varsayılan %{Math.round(v.denemeBasariEsigi * 100)})
              </span>
            )}
          </dd>
          <dt className="text-notr-600">Hüküm için en az soru</dt>
          <dd className="text-notr-900 tabular-nums">
            {e.konuHukmuIcinEnAzSoru}
            {e.konuHukmuIcinEnAzSoru !== v.konuHukmuIcinEnAzSoru && (
              <span className="text-notr-600 ml-2 text-xs">
                (varsayılan {v.konuHukmuIcinEnAzSoru})
              </span>
            )}
          </dd>
        </dl>

        <div className="grid gap-3 sm:grid-cols-2">
          <Alan
            etiket="Yeni başarı eşiği (%)"
            inputMode="numeric"
            value={basari}
            onChange={(ev) => setBasari(ev.target.value)}
            yardim={`%${s.denemeBasariEsigi.enAz * 100} – %${s.denemeBasariEsigi.enFazla * 100} arası. Boş bırakırsanız değişmez.`}
          />
          <Alan
            etiket="Yeni en az soru sayısı"
            inputMode="numeric"
            value={enAzSoru}
            onChange={(ev) => setEnAzSoru(ev.target.value)}
            yardim={`${s.konuHukmuIcinEnAzSoru.enAz} – ${s.konuHukmuIcinEnAzSoru.enFazla} arası. Boş bırakırsanız değişmez.`}
          />
        </div>

        {kaydet.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
          </p>
        )}
        {ozet && (
          <p role="status" className="rounded-kart bg-notr-50 text-notr-800 px-3 py-2 text-sm">
            {ozet}
          </p>
        )}

        <div>
          <Buton
            yukleniyor={kaydet.isPending}
            disabled={!basari.trim() && !enAzSoru.trim()}
            onClick={() => {
              setOzet(null);
              kaydet.mutate();
            }}
          >
            Eşikleri kaydet
          </Buton>
          <p className="text-notr-600 mt-1.5 text-xs">
            Değişiklik GEÇMİŞE dokunmaz: kayıtlı konu durumları aynen kalır, yeni eşik bundan
            sonraki deneme sonuçlarında işler. Her değişiklik eski ve yeni değeriyle denetim kaydına
            yazılır.
          </p>
        </div>
      </div>
    </Kart>
  );
}
